/**
 * Check if the code is running in a browser environment.
 */
export function isBrowser() {
  return typeof window !== 'undefined';
}

/**
 * @name formatCurrency
 * @description Format the currency based on the currency code
 */
export function formatCurrency(params: {
  currencyCode: string;
  locale: string;
  value: string | number;
}) {
  const [lang, region] = params.locale.split('-');

  return new Intl.NumberFormat(region ?? lang, {
    style: 'currency',
    currency: params.currencyCode,
  }).format(Number(params.value));
}

/**
 * @name isSafeRedirectPath
 * @description Checks if a path is safe for redirects (prevents open redirect attacks).
 * Safe paths must:
 * - Start with a single `/`
 * - NOT start with `//` (protocol-relative URLs)
 * - NOT contain `://` (absolute URLs)
 * - NOT contain backslash (URL normalization attacks)
 */
export function isSafeRedirectPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;

  // Must start with exactly one forward slash (relative path)
  if (!path.startsWith('/') || path.startsWith('//')) return false;

  // Must not contain protocol indicators
  if (path.includes('://')) return false;

  // Must not contain backslashes (can be normalized to forward slashes)
  if (path.includes('\\')) return false;

  return true;
}

/**
 * @name getSafeRedirectPath
 * @description Returns the path if safe, otherwise returns the fallback.
 * Use this to validate user-supplied redirect URLs to prevent open redirect attacks.
 */
export function getSafeRedirectPath(
  path: string | null | undefined,
  fallback: string,
): string {
  if (path && isSafeRedirectPath(path)) {
    return path;
  }

  return fallback;
}

/**
 * @name formatTimeToAmPm
 * @description Convert 24-hour time string (HH:mm or HH:mm:ss) to 12-hour AM/PM format
 * @example formatTimeToAmPm('14:30') // '2:30 PM'
 * @example formatTimeToAmPm('09:05:00', 'ar') // '2:30 م'
 */
export function formatTimeToAmPm(time: string, locale?: string): string {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const isAr = locale === 'ar';
  const period = hours >= 12 ? (isAr ? 'م' : 'PM') : (isAr ? 'ص' : 'AM');
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
}

/**
 * Resolve our app locale ('en' | 'ar') into a BCP-47 tag for `Intl`.
 * 'ar' → Arabic-language **Gregorian** calendar with Arabic-Indic digits
 * (NOT Hijri). Mirrors the customer `/book` date-strip reference so the
 * admin surfaces render dates identically.
 */
function intlLocale(locale?: string): string {
  return locale === 'ar' ? 'ar' : 'en-US';
}

/**
 * Merge base component options (day/month/year/hour/…) with caller options.
 * `Intl.DateTimeFormat` throws if `dateStyle`/`timeStyle` are combined with
 * any individual component option, so when the caller opts into either style
 * we drop the base components entirely and pass only the caller's options.
 */
function mergeDateTimeOptions(
  base: Intl.DateTimeFormatOptions,
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
  if (options && (options.dateStyle || options.timeStyle)) {
    return options;
  }

  return { ...base, ...options };
}

/**
 * @name formatLocaleDate
 * @description Locale-aware date formatter built on `Intl.DateTimeFormat`.
 * For `ar` it yields Arabic month/day words + Arabic-Indic numerals on the
 * Gregorian calendar (e.g. "٢٨ يونيو ٢٠٢٦"), matching the customer booking UI.
 * Accepts a Date, an ISO/`YYYY-MM-DD` string, or epoch ms. Returns '' on
 * invalid input so callers never render "Invalid Date".
 *
 * @example formatLocaleDate('2026-06-28', 'ar')                       // '٢٨ يونيو ٢٠٢٦'
 * @example formatLocaleDate('2026-06-28', 'en', { year: undefined })  // 'Jun 28'
 */
export function formatLocaleDate(
  value: string | number | Date | null | undefined,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat(
    intlLocale(locale),
    mergeDateTimeOptions(
      { day: 'numeric', month: 'short', year: 'numeric' },
      options,
    ),
  ).format(date);
}

/**
 * @name formatLocaleDateTime
 * @description Locale-aware date + 12-hour time formatter. Arabic renders
 * Arabic words/digits + "ص"/"م"; English renders "Jun 28, 2026, 5:00 PM".
 */
export function formatLocaleDateTime(
  value: string | number | Date | null | undefined,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat(
    intlLocale(locale),
    mergeDateTimeOptions(
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      },
      options,
    ),
  ).format(date);
}

/**
 * @name formatLocaleTime
 * @description Locale-aware TIME-only formatter. Unlike `formatTimeToAmPm`
 * (which returns Western digits), this renders Arabic-Indic numerals + "ص"/"م"
 * for `ar` via `Intl`, matching the customer `/book` date-strip and the Arabic
 * calendar grid. Accepts a Date, epoch ms, or an "HH:mm"/"HH:mm:ss" string.
 * `hour12` defaults to true (12-hour); pass false for 24-hour.
 *
 * @example formatLocaleTime('17:00', 'ar')          // '٥:٠٠ م'
 * @example formatLocaleTime('17:00', 'en')          // '5:00 PM'
 * @example formatLocaleTime('17:00', 'ar', false)   // '١٧:٠٠'
 */
export function formatLocaleTime(
  value: string | number | Date | null | undefined,
  locale?: string,
  hour12 = true,
): string {
  let date: Date | null;
  if (typeof value === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    const [h = 0, m = 0] = value.split(':').map(Number);
    date = new Date(2000, 0, 1, h, m);
  } else {
    date = toDate(value);
  }
  if (!date) return '';

  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: 'numeric',
    minute: '2-digit',
    hour12,
  }).format(date);
}

/**
 * @name formatLocaleNumber
 * @description Format a number with locale-aware digits/grouping. Arabic uses
 * Arabic-Indic numerals. Useful for counts and table figures so the digits
 * match the surrounding Arabic copy. Amounts paired with a currency glyph
 * should keep using `formatCurrency`/`toFixed` per the CurrencyIcon contract.
 */
export function formatLocaleNumber(
  value: number | string | null | undefined,
  locale?: string,
  options?: Intl.NumberFormatOptions,
): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (num === null || num === undefined || Number.isNaN(num)) return '';
  return new Intl.NumberFormat(intlLocale(locale), options).format(num);
}

/**
 * Coerce a Date | ISO string | `YYYY-MM-DD` | epoch-ms into a valid Date,
 * or `null` if it can't be parsed. A bare `YYYY-MM-DD` is parsed as local
 * wall-clock midnight (not UTC) so it never renders as the previous day.
 */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(y as number, (m as number) - 1, d as number);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @name escapeCsvCell
 * @description Escape a single CSV cell for safe consumption by spreadsheet
 * apps. Handles two distinct concerns:
 *
 * 1. **RFC 4180 quoting** — a cell containing a comma, double quote, CR or LF
 *    is wrapped in quotes with inner quotes doubled.
 * 2. **Formula injection (CWE-1236)** — Excel / Sheets / LibreOffice execute a
 *    cell whose text begins with `=`, `+`, `-`, `@`, or a leading tab/CR. Since
 *    exports carry attacker-influenceable free text (invoice + payment
 *    descriptions, cardholder names, coach names), such a cell is prefixed with
 *    a single quote so the app treats it as literal text. The `'` is a
 *    spreadsheet-level escape and is not shown as part of the value.
 *
 * Note that quoting alone does NOT prevent execution — `"=cmd()"` still runs —
 * so the prefix must be applied inside the quotes, before quoting.
 *
 * A cell that is a well-formed number (including a negative amount such as a
 * refund's `-40.00`, or exponent notation) is exempt from the prefix: it cannot
 * carry a payload, and prefixing it would turn it into text and break numeric
 * analysis in the spreadsheet.
 *
 * @example escapeCsvCell('=1+1')        // `"'=1+1"`
 * @example escapeCsvCell('-40.00')      // `-40.00`  (numeric, left alone)
 * @example escapeCsvCell('-40 refund')  // `"'-40 refund"`
 * @example escapeCsvCell('Ali, Ahmed')  // `"Ali, Ahmed"`
 * @example escapeCsvCell('plain')       // `plain`
 */
export function escapeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';

  let str = String(value);

  const isPlainNumber = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(str);

  if (!isPlainNumber && /^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }

  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

/**
 * @name toCsvRow
 * @description Join one row of cells into a CSV line, escaping each cell via
 * {@link escapeCsvCell}.
 */
export function toCsvRow(cells: (string | number | null | undefined)[]): string {
  return cells.map(escapeCsvCell).join(',');
}

/**
 * @name CSV_BOM
 * @description UTF-8 byte-order mark. Excel on Windows needs this to decode a
 * UTF-8 CSV as UTF-8; the `charset` in the MIME type alone is not enough, and
 * without it Arabic content renders as mojibake. Prepend to the CSV string when
 * constructing the Blob.
 */
export const CSV_BOM = '﻿';

/**
 * @name VAT_RATE_BY_CURRENCY
 * @description Statutory VAT rate (whole-number %) per ISO currency code,
 * mirroring the DB helper public.vat_rate_for_currency — keep the two in
 * sync. Stored amounts across the app are VAT-INCLUSIVE, so consumers derive
 * pre-VAT as gross / (1 + rate / 100). Unknown currencies map to 0%.
 */
export const VAT_RATE_BY_CURRENCY: Record<string, number> = {
  SAR: 15,
  AED: 5,
  JOD: 16,
  BHD: 10,
  KWD: 0,
  QAR: 0,
};

/**
 * @name vatRateForCurrency
 * @description Statutory VAT rate (%) for a currency code; 0 for unknown.
 */
export function vatRateForCurrency(
  currency: string | null | undefined,
): number {
  return VAT_RATE_BY_CURRENCY[(currency ?? 'SAR').toUpperCase()] ?? 0;
}
